import { useState } from 'react';
import {
  Button,
  ButtonGroup,
  Flex,
  Heading,
  IconExclamationCircle,
  Page,
  PageContent,
  PageTitle,
  Spinner,
  TextButton,
} from '@zillow/constellation';
import { RENTAL_MANAGER_WEB } from '../../constants/paths';
import { useQuery } from '@apollo/client';
import {
  APPLICATION_MANAGER_QUERY,
  getIsCurrentUserAnAdmin,
  getIsCurrentUserAnApplicationManager,
  getNeedsApplicationManager,
} from '../../utils/businessVerification';
import ApplicationManagerModal from '../../components/ApplicationManagerModal/ApplicationManagerModal';
import { useSelector } from 'react-redux';
import { userIdIreneSelector } from '../../selectors/index';
import { useHistory } from 'react-router-dom';
import { getApplicationsTabPath } from '../../constants/urls';
import RequestPermissionButton from './RequestPermissionButton';
import MissingPermissionsContent from './MissingPermissionsContent';
import { pathsManifest } from '@zg-rentals/shields-js';
import useQueryParams from '../../hooks/useQueryParams';

function useOnApplicationManagerClose(refetchApplicationManagerQuery, setIsApplicationManagerModalOpen) {
  const history = useHistory();
  const { propertyId } = useQueryParams();
  return async () => {
    // intentionally closing modal first, before redirecting
    setIsApplicationManagerModalOpen(false);
    const result = await refetchApplicationManagerQuery();
    const needsApplicationManager = getNeedsApplicationManager(result.data);
    if (!needsApplicationManager) {
      history.replace(getApplicationsTabPath(propertyId));
    }
  };
}

function useOnApplicationManagerClick(setIsApplicationManagerModalOpen) {
  return () => {
    setIsApplicationManagerModalOpen(true);
  };
}

function getPageTitle(isInUserManagementCompany, needsApplicationManager, isCurrentUserAnAdmin) {
  if (isInUserManagementCompany && needsApplicationManager && isCurrentUserAnAdmin) {
    return 'Your account needs an application manager to access applications';
  } else if (isInUserManagementCompany && (!isCurrentUserAnAdmin || !needsApplicationManager)) {
    return 'You need permission to access applications';
  } else {
    return 'Try logging in with a different email address';
  }
}

export default function MissingPermissionsPage() {
  const userId = useSelector(userIdIreneSelector);
  const [isApplicationManagerModalOpen, setIsApplicationManagerModalOpen] = useState(false);
  const { propertyId } = useQueryParams();
  const { loading, data, refetch } = useQuery(APPLICATION_MANAGER_QUERY, {
    variables: {
      propertyId,
    },
  });
  const onApplicationManagerClose = useOnApplicationManagerClose(refetch, setIsApplicationManagerModalOpen);
  const onApplicationManagerClick = useOnApplicationManagerClick(setIsApplicationManagerModalOpen);
  const needsApplicationManager = getNeedsApplicationManager(data);
  const isCurrentUserAnAdmin = getIsCurrentUserAnAdmin(data, userId);
  const isCurrentUserAnApplicationManager = getIsCurrentUserAnApplicationManager(data, userId);
  const userManagementCompanyId = data?.user?.userManagementCompany?.id;
  const isInUserManagementCompany = Boolean(userManagementCompanyId);
  const listPagePath = getApplicationsTabPath(propertyId);
  const loginPath = pathsManifest.zillow.signInPage(listPagePath, '');
  return (
    <Page>
      {loading && (
        <PageContent>
          <Flex display="flex" justifyContent="center">
            <Spinner size="lg" />
          </Flex>
        </PageContent>
      )}
      {!loading && (
        <>
          <PageContent>
            <IconExclamationCircle size="lg" fontColor="blue600" />
          </PageContent>
          <PageTitle
            heading={
              <Heading level={1}>
                {getPageTitle(isInUserManagementCompany, needsApplicationManager, isCurrentUserAnAdmin)}
              </Heading>
            }
          />
          <PageContent>
            <MissingPermissionsContent data={data} />
          </PageContent>
          <PageContent>
            <ButtonGroup aria-label="Missing application permissions actions">
              {!isInUserManagementCompany && (
                <Button buttonType="primary" id="switch-accounts-link" as="a" href={loginPath}>
                  Switch accounts
                </Button>
              )}
              {isInUserManagementCompany && needsApplicationManager && isCurrentUserAnAdmin && (
                <Button
                  buttonType="primary"
                  id="become-application-manager-button"
                  onClick={onApplicationManagerClick}
                  disabled={!needsApplicationManager}
                >
                  Become application manager
                </Button>
              )}
              {isInUserManagementCompany && !needsApplicationManager && !isCurrentUserAnApplicationManager && (
                <RequestPermissionButton userManagementCompanyId={userManagementCompanyId} />
              )}
              <TextButton as="a" id="go-to-your-properties-button" href={RENTAL_MANAGER_WEB}>
                Go to your properties
              </TextButton>
            </ButtonGroup>
          </PageContent>
          <ApplicationManagerModal
            isOpen={isApplicationManagerModalOpen}
            onClose={onApplicationManagerClose}
            employees={data?.user?.userManagementCompany?.employees}
            street={data?.property?.street}
          />
        </>
      )}
    </Page>
  );
}
